// src/components/heatmap/LgsMarkers.tsx
import React, { useEffect, useState, useMemo } from "react";
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useLgsPoints, useLgsLayerEnabled } from "@/lib/heatmapStore";
import lgsPng from "@/assets/lgs.png";

const lgsIcon = L.icon({
  iconUrl: lgsPng,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  popupAnchor: [0, -14],
  className: "lgs-map-icon",
});

// Small helper – sanitize / ensure protocol
function normalizeUrl(raw?: string | null) {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

type LgsPoint = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  address?: string;
  phone?: string;
  website?: string;
  source?: "storeIndex" | "userProfile";
};

export default function LgsMarkers() {
  const enabled = useLgsLayerEnabled();
  const storePoints = useLgsPoints() as LgsPoint[]; // existing heatmap store points
  const [userLgsPoints, setUserLgsPoints] = useState<LgsPoint[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Fetch LGS user profiles once (or on interval if you like)
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    async function loadLgsUsers() {
      try {
        // Primary predicate: isLGS == true
        // If your schema uses another field (e.g. roles.lgs, claims.isLGS) adjust below.
        const q1 = query(
          collection(db, "users"),
          where("isLGS", "==", true),
          limit(500)
        );

        const snap = await getDocs(q1);
        if (cancelled) return;

        const out: LgsPoint[] = [];
        snap.forEach((docSnap) => {
          const d: any = docSnap.data();
          const lat = d?.location?.lat;
          const lng = d?.location?.lng;
            if (typeof lat !== "number" || typeof lng !== "number") return;

          const name =
            d.store_name ||
            d.business_name ||
            d.display_name ||
            d.username ||
            "LGS";

          out.push({
            id: `user:${docSnap.id}`,
            name,
            lat,
            lng,
            address: d.store_address || d.address || d.location?.address || undefined,
            phone: d.store_phone || d.phone || undefined,
            website:
              d.website ||
              d.store_website ||
              d.url ||
              (d.links?.website ? d.links.website : undefined),
            source: "userProfile",
          });
        });

        setUserLgsPoints(out);
      } catch {
        // silent fail; keep existing points
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }

    void loadLgsUsers();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Merge + dedupe
  const mergedPoints = useMemo(() => {
    if (!enabled) return [] as LgsPoint[];

    const byCoordOrName = new Map<string, LgsPoint>();

    function keyFor(p: LgsPoint) {
      // Round coords to ~11m precision so overlapping sources merge
      const latR = p.lat.toFixed(3);
      const lngR = p.lng.toFixed(3);
      const nameK = (p.name || "").toLowerCase().trim();
      return `${latR}|${lngR}|${nameK}`;
    }

    [...storePoints.map(p => ({ ...p, source: "storeIndex" as const })), ...userLgsPoints].forEach(
      (p) => {
        if (typeof p.lat !== "number" || typeof p.lng !== "number") return;
        const k = keyFor(p);
        if (!byCoordOrName.has(k)) {
          byCoordOrName.set(k, p);
        } else {
          // Merge missing fields preference: storeIndex > userProfile
          const existing = byCoordOrName.get(k)!;
          byCoordOrName.set(k, {
            ...existing,
            address: existing.address || p.address,
            phone: existing.phone || p.phone,
            website: existing.website || p.website,
          });
        }
      },
    );

    return Array.from(byCoordOrName.values());
  }, [enabled, storePoints, userLgsPoints]);

  if (!enabled || !mergedPoints.length) {
    return null;
  }

  return (
    <>
      {/* Popup theme overrides (scoped to .lgs-popup) */}
      <style>{`
        .leaflet-popup.lgs-popup .leaflet-popup-content-wrapper {
          background: #111827;
          color: #fbbf24;
          border: 1px solid #374151;
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0,0,0,.45);
        }
        .leaflet-popup.lgs-popup .leaflet-popup-tip {
          background: #111827;
          box-shadow: none;
          border: 1px solid #374151;
        }
        .lgs-popup a { color: #fbbf24; text-decoration: none; }
        .lgs-popup a:hover { text-decoration: underline; filter: brightness(1.05); }
        .lgs-popup a:visited, .lgs-popup a:active { color: #a78bfa; }
      `}</style>

      {mergedPoints.map((p) => {
        const href = normalizeUrl(p.website);
        const hasSite = !!href;

        return (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={lgsIcon}>
            <Popup className="lgs-popup" closeButton={false}>
              <div className="lgs-popup-inner text-[11px] leading-snug">
                <div className="flex items-center gap-2 mb-2 pb-1 border-b border-gray-600/50">
                  <img
                    src={lgsPng}
                    alt="LGS"
                    className="w-6 h-6 object-contain shrink-0"
                    draggable={false}
                  />
                  <div className="uppercase tracking-wide text-[10px] font-medium">
                    {p.source === "userProfile" ? "LGS User" : "Local Game Store"}
                  </div>
                </div>

                <h2 className="text-[15px] font-semibold mb-1 leading-tight">
                  {hasSite ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-words"
                      title="Open website (new tab)"
                    >
                      {p.name}
                    </a>
                  ) : (
                    <span>{p.name}</span>
                  )}
                </h2>

                <div className="space-y-1 mb-2">
                  {p.address && (
                    <div className="whitespace-pre-line opacity-90">{p.address}</div>
                  )}
                  {p.phone && <div className="opacity-80">{p.phone}</div>}
                </div>

                <div className="mt-1 text-[10px] opacity-70 flex items-center gap-2">
                  <span>
                    {p.lat.toFixed(4)}, {p.lng.toFixed(4)}
                  </span>
                  <span className="uppercase tracking-wide text-[9px] px-1.5 py-0.5 rounded bg-gray-700/50">
                    {p.source === "userProfile" ? "USER" : "DB"}
                  </span>
                </div>

                {hasSite && (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block mt-2"
                    title="Visit website"
                  >
                    <div className="relative border border-gray-700 rounded overflow-hidden bg-neutral-800/70 h-32">
                      <iframe
                        src={href}
                        title={`${p.name} preview`}
                        loading="lazy"
                        sandbox="allow-same-origin allow-scripts allow-popups"
                        className="w-full h-full pointer-events-none"
                        style={{ border: 0, background: "#1f1f1f" }}
                      />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 text-[10px]">
                        Click to open site
                      </div>
                    </div>
                  </a>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}

      {!loaded && (
        <div style={{ display: "none" }} data-lgs-users-loading="true" />
      )}
    </>
  );
}

